import React from 'react';

import useDeviceOrientation from 'hooks/use-device-orientation';
import useWindowWidth from 'hooks/use-window-width';

const SPOTLIGHT_RADIUS = 200;

const Spotlight = ({ inView }) => {
  const spotlightAnimationRef = React.useRef();
  const windowWidth = useWindowWidth();
  const orientation = useDeviceOrientation();

  React.useEffect(() => {
    if (!spotlightAnimationRef.current) {
      return;
    }
    if (inView) {
      spotlightAnimationRef.current.resume();
    } else {
      spotlightAnimationRef.current.pause();
    }
  }, [inView]);

  React.useEffect(() => {
    const section = document.getElementById('spotlight-container');

    // eslint-disable-next-line no-use-before-define
    spotlightAnimationRef.current = createSpotlightAnimation(section);

    return () => {
      spotlightAnimationRef.current = null;
    };
  }, [windowWidth, orientation]);

  const [hasPlayedInitialAnimation, setHasPlayedInitialAnimation] = React.useState(false);

  React.useEffect(() => {
    const section = document.getElementById('spotlight-container');

    const scrollListener = (evt) => {
      const sectionToTop =
        window.pageYOffset -
        section.getBoundingClientRect().height +
        section.getBoundingClientRect().top;

      const initialAnimationOffset = sectionToTop + section.getBoundingClientRect().height * 0.75;
      const sectionEnd = sectionToTop + section.getBoundingClientRect().height;

      if (
        !hasPlayedInitialAnimation &&
        window.scrollY >= initialAnimationOffset &&
        window.scrollY < sectionEnd
      ) {
        spotlightAnimationRef.current.playInitialAnimation();
        setHasPlayedInitialAnimation(true);
      }
    };
    if (!hasPlayedInitialAnimation) {
      document.addEventListener('scroll', scrollListener);
    }
    if (hasPlayedInitialAnimation) {
      document.removeEventListener('scroll', scrollListener);
    }

    return () => {
      document.removeEventListener('scroll', scrollListener);
    };
  }, [hasPlayedInitialAnimation, windowWidth, orientation]);

  return (
    <div id="spotlight">
      <svg id="spotlight-svg" viewBox="0 0 400 400">
        <defs>
          <radialGradient id="grad1" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
            <stop offset="0%" style={{ stopColor: 'rgb(26, 26, 26)', stopOpacity: 0 }} />
            <stop offset="4%" style={{ stopColor: 'rgb(26, 26, 26)', stopOpacity: 0 }} />
            <stop offset="10%" style={{ stopColor: 'rgb(26, 26, 26)', stopOpacity: 0.98 }} />
          </radialGradient>
        </defs>
        <rect id="spotlight-rect" x="200" y="200" width="400" height="400" fill="url(#grad1)" />
      </svg>
    </div>
  );
};

export default Spotlight;

const remToPx = (rem) => {
  return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
};

// Values relative to the section
const RECORDED_ANIMATION_POINTS = [
  [45, 19], // are h
  [87, 19], // ard
  [22, 45], // bottom left
  [116, 45], // bottom right
];

const REMS_PER_S = 100;
const dist = (pa, pb) => Math.sqrt((pa[1] - pb[1]) ** 2 + (pa[0] - pb[0]) ** 2);

const RECORDED_ANIMATION_SEGMENTS = [];
for (let i = 0, curDist = 0; i < RECORDED_ANIMATION_POINTS.length - 1; i += 1) {
  const d = dist(RECORDED_ANIMATION_POINTS[i], RECORDED_ANIMATION_POINTS[i + 1]);
  const start = curDist;
  const end = curDist + d;
  const tStartMs = (start / REMS_PER_S) * 1000;
  const tEndMs = (end / REMS_PER_S) * 1000;

  RECORDED_ANIMATION_SEGMENTS.push({
    pStart: RECORDED_ANIMATION_POINTS[i],
    pEnd: RECORDED_ANIMATION_POINTS[i + 1],
    tStartMs,
    tEndMs,
  });

  curDist += d;
}

const getRecordedMotionPositionAtTime = (tMs, sectionStartYPx) => {
  const segment = RECORDED_ANIMATION_SEGMENTS.find((s) => s.tStartMs <= tMs && s.tEndMs >= tMs);

  if (!segment) {
    return null;
  }

  // [0; 1]
  const segmentTau = (tMs - segment.tStartMs) / (segment.tEndMs - segment.tStartMs);
  const pos = [
    remToPx(segment.pStart[0] + (segment.pEnd[0] - segment.pStart[0]) * segmentTau),
    remToPx(segment.pStart[1] + (segment.pEnd[1] - segment.pStart[1]) * segmentTau) +
      sectionStartYPx,
  ];
  return pos;
};

const createSpotlightAnimation = (sectionEl) => {
  const spotlight = document.getElementById('spotlight');

  const spotlightSvg = document.getElementById('spotlight-svg');
  const spotlightRect = document.getElementById('spotlight-rect');

  const width = document.documentElement.clientWidth;
  const height = document.documentElement.clientHeight;

  const rect = sectionEl.getBoundingClientRect();
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  const sectionTopOffset = rect.top + scrollTop;

  const moveSpotlight = (gx, gy) => {
    const spotlightContainerSize = 2.5 * Math.max(width, height);
    spotlight.style.left = `${gx - SPOTLIGHT_RADIUS - spotlightContainerSize / 2}px`;
    spotlight.style.top = `${
      gy - sectionTopOffset - SPOTLIGHT_RADIUS - spotlightContainerSize / 2
    }px`;
    spotlight.style.width = `${spotlightContainerSize}px`;
    spotlight.style.height = `${spotlightContainerSize}px`;

    spotlightRect.setAttribute('width', `${spotlightContainerSize}`);
    spotlightRect.setAttribute('height', `${spotlightContainerSize}`);
    spotlightSvg.setAttribute('viewBox', `0 0 ${spotlightContainerSize} ${spotlightContainerSize}`);
  };

  const onMouseMove = (e) => {
    let gx = 0;
    let gy = 0;

    if (e.pageX || e.pageY) {
      gx = e.pageX;
      gy = e.pageY;
    } else if (e.clientX || e.clientY) {
      gx = e.clientX + document.body.scrollLeft;
      gy = e.clientY + document.body.scrollTop;
    }
    moveSpotlight(gx, gy);
  };

  let hasStarted = false;
  const playInitialAnimation = () => {
    if (hasStarted) {
      return;
    }
    hasStarted = true;
    let currentTimeMs = 0;
    let lastCalled = performance.now();
    let currentPosition = [
      remToPx(RECORDED_ANIMATION_POINTS[0][0]),
      remToPx(RECORDED_ANIMATION_POINTS[0][1]) + sectionTopOffset,
    ];

    moveSpotlight(currentPosition[0], currentPosition[1]);

    const recordedMove = () => {
      const now = performance.now();
      const msPassed = now - lastCalled;

      currentPosition = getRecordedMotionPositionAtTime(currentTimeMs, sectionTopOffset);

      if (!currentPosition) {
        return;
      }
      currentTimeMs += msPassed;
      moveSpotlight(currentPosition[0], currentPosition[1]);

      lastCalled = performance.now();
      window.requestAnimationFrame(recordedMove);
    };

    currentTimeMs = 0;
    lastCalled = performance.now();
    recordedMove();
  };

  const pause = () => {
    document.removeEventListener('mousemove', onMouseMove);
  };

  const resume = () => {
    moveSpotlight(width * 0.5, window.pageYOffset + height * 0.5 - SPOTLIGHT_RADIUS);

    document.addEventListener('mousemove', onMouseMove, false);
  };

  return {
    pause,
    resume,
    playInitialAnimation,
  };
};
